import { ApiError } from '@/lib/api';
import type {
  NoyauCode,
  NpcBehaviour,
  NpcCard,
  NpcExpectedNoyau,
  NpcKind,
  NpcPrefillReason,
  NpcRefusal,
} from '@matter_extractor/shared';
import {
  NOYAU_CODES,
  NPC_BEHAVIOUR_LABELS,
  NPC_KIND_LABELS,
  NPC_PREFILL_REASON_LABELS,
  OUT_OF_BASE,
  npcPrefillReasonSchema,
  npcRefusalSchema,
} from '@matter_extractor/shared';

/**
 * The words of the NPC section (npc.md §12, issue #210), PURE and in one place.
 *
 * The régime of `persona-labels.ts` and `versioned-labels.ts`: a refusal is worded from its CODE,
 * never from the server's sentence (an English developer string bound for the logs), and what
 * the contract already names - the three kinds, the eight behaviours, the reasons of a refused
 * prefill - is read from `shared` and not restated.
 */

/* ----------------------------------------------------------------- the card */

/** The initial of the avatar: the FIRST NAME's, never the operator's name (§12). */
export function npcInitial(firstName: string): string {
  return firstName.trim().charAt(0).toUpperCase();
}

/**
 * The colour of the avatar says the KIND (§12): night blue for a famous person, yellow for a
 * written profile, green for a real session. Each on its `-tint` and `-deep` pair, the pair every
 * pill of the application wears - yellow is only ever INK at its `-deep` value.
 */
export const NPC_KIND_AVATAR_CLASS: Readonly<Record<NpcKind, string>> = {
  famous: 'bg-bleu-nuit-tint text-bleu-nuit',
  written: 'bg-jaune-tint text-jaune-deep',
  session: 'bg-vert-tint text-vert-deep',
};

/** « 1 source », « 4 sources ». */
export function sourcesWord(count: number): string {
  return `${count} ${count > 1 ? 'sources' : 'source'}`;
}

/** « Célèbre · Marie · 1 source »: what stands under the name on a card. */
export function npcSubtitle(card: Pick<NpcCard, 'kind' | 'firstName' | 'sourceCount'>): string {
  return `${NPC_KIND_LABELS[card.kind]} · ${card.firstName} · ${sourcesWord(card.sourceCount)}`;
}

/** « Joué par 12 rejeux », « Joué par 1 rejeu », « Pas encore joué » (§3.4, §12). */
export function npcPlayedSentence(replayCount: number): string {
  if (replayCount === 0) return 'Pas encore joué';
  return `Joué par ${replayCount} ${replayCount > 1 ? 'rejeux' : 'rejeu'}`;
}

/** « Jouée par 3 rejeux », for a version of the history: the feminine, and nothing at zero. */
export function versionPlayedSentence(count: number): string {
  return `Jouée par ${count} ${count > 1 ? 'rejeux' : 'rejeu'}`;
}

/** « Le moment : ... », the line a card and a fiche read the moment on. */
export function npcMomentLine(moment: string): string {
  return `Le moment : ${moment}`;
}

/* ----------------------------------------------------------- expected noyau */

/**
 * The options of the expected noyau, in the order of the mockup: the six triplets, « hors base »,
 * and « aucun » (H6). The value is what the contract stores - `null` for none.
 */
export const NPC_EXPECTED_OPTIONS: readonly {
  readonly value: NpcExpectedNoyau | null;
  readonly label: string;
}[] = [
  ...NOYAU_CODES.map((code) => ({ value: code as NpcExpectedNoyau, label: code })),
  { value: OUT_OF_BASE, label: 'hors base' },
  { value: null, label: 'aucun' },
];

/** What a card and a chip say of the expected noyau: « SGP », « hors base », « sans noyau attendu ». */
export function npcExpectedLabel(expected: NpcExpectedNoyau | null): string {
  if (expected === null) return 'sans noyau attendu';
  return expected === OUT_OF_BASE ? 'hors base' : expected;
}

/** Is it one of the six triplets - the narrowing that picks the noyau's own pill. */
export function isTripletExpected(expected: NpcExpectedNoyau | null): expected is NoyauCode {
  return expected !== null && expected !== OUT_OF_BASE;
}

/* ---------------------------------------------------------------- behaviours */

/** The label of a behaviour, lower-case as a tag in a sentence: « la généraliste ». */
export function behaviourTag(behaviour: NpcBehaviour): string {
  return NPC_BEHAVIOUR_LABELS[behaviour].toLowerCase();
}

/* ------------------------------------------------------------------ sources */

/** The words of a text, by blanks: « 872 mots ». */
export function wordCount(text: string): number {
  const trimmed = text.trim();
  return trimmed === '' ? 0 : trimmed.split(/\s+/).length;
}

/** `6 095`, grouped by the thin space France groups thousands with. */
export function groupedNumber(value: number): string {
  return String(Math.round(value)).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

/** « 252 / 4000 caractères », the counter under a bounded field. */
export function counterLabel(used: number, max: number): string {
  return `${groupedNumber(used)} / ${groupedNumber(max)} caractères`;
}

/* ------------------------------------------------------------ the refusals */

/** The body of a refusal inside a failed call, or null when it was none. */
export function npcRefusalOf(error: unknown): NpcRefusal | null {
  if (!(error instanceof ApiError)) return null;
  const parsed = npcRefusalSchema.safeParse(error.details);
  return parsed.success ? parsed.data : null;
}

/**
 * The French sentence of a refusal an NPC's save, deletion or restoration can answer (§17).
 *
 * Only the codes a save, a deletion or a restoration can meet: the launch's own
 * (`bench-empty`, `passes-out-of-range`...) are worded by `lib/npc-launch.ts`. `null` is « no sentence of mine for this one », and the
 * caller falls back to the generic line of its gesture.
 */
export function npcRefusalSentence(error: unknown): string | null {
  const refusal = npcRefusalOf(error);
  if (refusal === null) return null;
  switch (refusal.code) {
    case 'source-too-long':
      return 'Les textes des sources dépassent 100 000 caractères ensemble. Raccourcissez-en un.';
    case 'npc-retired':
      return 'Ce NPC est retiré. Restaurez-le avant de le modifier.';
    case 'npc-unknown':
      return 'Ce NPC n’existe plus.';
    case 'bench-unknown':
      return 'Ce banc n’existe plus.';
    case 'name-taken':
      return refusal.name === undefined
        ? 'Ce nom est déjà porté. Choisissez-en un autre.'
        : `« ${refusal.name} » porte déjà ce nom. Choisissez-en un autre.`;
    case 'turn-in-flight':
      return 'Un tour se joue sur l’un de ses rejeux : rien n’a été supprimé. Réessayez dans quelques secondes.';
    case 'session-in-trash':
      return 'Cette session est à la corbeille : restaurez-la d’abord.';
    case 'prefill-refused':
      return refusal.reason === undefined
        ? 'Cette session ne peut pas donner un NPC.'
        : `${NPC_PREFILL_REASON_LABELS[refusal.reason]}.`;
    default:
      return null;
  }
}

/**
 * The reason a prefill was refused, read off the 409 `prefill-refused`, or null when the failure is
 * something else (a closed replay, a session that does not exist, a network error).
 */
export function prefillReasonOf(error: unknown): NpcPrefillReason | null {
  const refusal = npcRefusalOf(error);
  if (refusal === null || refusal.code !== 'prefill-refused') return null;
  const reason = npcPrefillReasonSchema.safeParse(refusal.reason);
  return reason.success ? reason.data : null;
}

/** The sentence under the title when a prefill is refused for a reason we know. */
export function prefillRefusedSentence(reason: NpcPrefillReason): string {
  return `${NPC_PREFILL_REASON_LABELS[reason]}.`;
}
